import React, { useEffect } from "react";
import Layout from "../../components/layout";
import { Col, Form, Input, Row, Button, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import { MedicineBoxOutlined, PlusCircleOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

const injectStyles = () => {
  if (document.getElementById("simple-form-styles")) return;
  const el = document.createElement("style");
  el.id = "simple-form-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');
    .sf-root{font-family:'DM Sans',sans-serif;background:${SURFACE};min-height:100vh}
    .sf-wrap{max-width:580px;margin:0 auto;padding:32px 24px 64px}
    .sf-hero{background:linear-gradient(135deg,${SLATE} 0%,${SLATE_MID} 100%);border-radius:24px;padding:36px 44px;position:relative;overflow:hidden;margin-bottom:32px}
    .sf-hero::before{content:'';position:absolute;top:-80px;right:-80px;width:300px;height:300px;border-radius:50%;background:radial-gradient(circle,${TEAL}44 0%,transparent 70%);filter:blur(32px);pointer-events:none}
    .sf-hero::after{content:'';position:absolute;bottom:-60px;left:-40px;width:200px;height:200px;border-radius:50%;background:radial-gradient(circle,${EMERALD}33 0%,transparent 70%);filter:blur(26px);pointer-events:none}
    .sf-eyebrow{display:inline-block;padding:4px 12px;border-radius:20px;background:${TEAL_LIGHT};color:${EMERALD};font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;font-family:'Sora',sans-serif;margin-bottom:12px}
    .sf-section{background:${WHITE};border-radius:20px;box-shadow:0 4px 20px rgba(0,0,0,.06);padding:28px 28px 20px}
    .sf-section-head{display:flex;align-items:center;gap:10px;margin-bottom:24px;padding-bottom:16px;border-bottom:1.5px solid #f1f5f9}
    .sf-section-icon{width:36px;height:36px;border-radius:10px;flex-shrink:0;background:${TEAL_LIGHT};color:${EMERALD};display:flex;align-items:center;justify-content:center;font-size:16px}
    .sf-root .ant-form-item{margin-bottom:20px !important}
    .sf-root .ant-form-item-label{padding-bottom:4px !important;line-height:1 !important}
    .sf-root .ant-form-item-label>label{font-size:12px !important;font-weight:600 !important;color:${SLATE_MID} !important;height:20px !important;line-height:20px !important}
    .sf-root .ant-form-item-explain{min-height:0 !important;font-size:11px !important}
    .sf-root .ant-input{border-radius:10px !important;height:44px !important;line-height:44px !important;border:1.5px solid #e2e8f0 !important;padding:0 12px !important;font-family:'DM Sans',sans-serif !important;font-size:13px !important;box-sizing:border-box !important;transition:border-color .2s,box-shadow .2s !important}
    .sf-root .ant-input:hover,.sf-root .ant-input:focus{border-color:${TEAL} !important;box-shadow:0 0 0 3px ${TEAL}22 !important}
    .sf-root .ant-input-affix-wrapper{border-radius:10px !important;height:44px !important;border:1.5px solid #e2e8f0 !important;padding:0 12px !important;display:flex !important;align-items:center !important;font-family:'DM Sans',sans-serif !important;font-size:13px !important;box-sizing:border-box !important;transition:border-color .2s,box-shadow .2s !important}
    .sf-root .ant-input-affix-wrapper .ant-input{height:100% !important;border:none !important;box-shadow:none !important;padding:0 !important;background:transparent !important}
    .sf-root .ant-input-affix-wrapper:hover,.sf-root .ant-input-affix-wrapper-focused{border-color:${TEAL} !important;box-shadow:0 0 0 3px ${TEAL}22 !important}
    .sf-hint{background:${TEAL_LIGHT};border-radius:12px;padding:12px 16px;display:flex;align-items:flex-start;gap:10px;margin-bottom:24px}
    .sf-hint-dot{width:6px;height:6px;border-radius:50%;background:${EMERALD};flex-shrink:0;margin-top:5px}
    .sf-submit{height:52px !important;width:100% !important;border-radius:14px !important;font-size:15px !important;font-weight:700 !important;font-family:'Sora',sans-serif !important;background:linear-gradient(135deg,${EMERALD},${TEAL}) !important;border:none !important;color:${WHITE} !important;box-shadow:0 8px 20px ${TEAL}44 !important;transition:transform .2s,box-shadow .2s !important}
    .sf-submit:hover{transform:translateY(-2px) !important;box-shadow:0 14px 32px ${TEAL}55 !important}
    @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
    .sf-fade{animation:fadeUp .5s ease both}
    .sf-fade-1{animation-delay:.05s}
    .sf-fade-2{animation-delay:.12s}
  `;
  document.head.appendChild(el);
};

function Service() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();

  useEffect(() => { injectStyles(); }, []);

  const onFinish = async (values) => {
    try {
      dispatch(showLoading());
      const response = await axios.post(
        "/api/admin/service",
        { ...values, userId: user._id },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (response.data.success) {
        toast.success(response.data.message);
        navigate("/");
      } else {
        toast.error(response.data.message);
      }
    } catch {
      dispatch(hideLoading());
      toast.error("Something went wrong");
    }
  };

  return (
    <Layout>
      <div className="sf-root">
        <div className="sf-wrap">

          <div className="sf-hero sf-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="sf-eyebrow"><MedicineBoxOutlined style={{ marginRight: 5 }} />Configuration</div>
              <Title level={2} style={{ margin: "6px 0 4px", fontFamily: "'Sora',sans-serif", color: WHITE, fontWeight: 800 }}>
                Add Service
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                Define a new medical service offered at the clinic for appointment booking
              </Text>
            </div>
          </div>

          <div className="sf-section sf-fade sf-fade-1">
            <div className="sf-section-head">
              <div className="sf-section-icon"><MedicineBoxOutlined /></div>
              <Title level={5} style={{ margin: 0, fontFamily: "'Sora',sans-serif", color: SLATE, fontWeight: 700 }}>
                Service Details
              </Title>
            </div>

            <div className="sf-hint">
              <div className="sf-hint-dot" />
              <Text style={{ color: EMERALD, fontSize: 12, lineHeight: 1.5 }}>
                This service will be selectable when assigning doctors to appointments and visible to patients during booking.
              </Text>
            </div>

            <Form layout="vertical" onFinish={onFinish} requiredMark={false}>
              <Row gutter={[20, 0]}>
                <Col xs={24}>
                  <Form.Item label="Service Name" name="service"
                    rules={[{ required: true, message: "Service name is required" },  { min: 3, message: "Service must be at least 3 characters" }]}>
                    <Input placeholder="e.g. General Consultation, Eye Care, Dental"
                      prefix={<MedicineBoxOutlined style={{ color: MUTED }} />} />
                  </Form.Item>
                </Col>
              </Row>
              <Button className="sf-submit sf-fade sf-fade-2" htmlType="submit" icon={<PlusCircleOutlined />}>
                Add Service
              </Button>
            </Form>
          </div>

        </div>
      </div>
    </Layout>
  );
}

export default Service;